"use client"

import { useState } from "react"
import { useLanguage } from "../../contexts/LanguageContext"
import { Icons } from "../../components/Icons"
import "./App.css"
import AbacusIntroduction from "./components/AbacusIntroduction"
import AbacusArithmetic from "./components/AbacusArithmetic"
import NumberLearning1to4 from "./components/NumberLearning1to4"
import NumberLearning5to8 from "./components/NumberLearning5to8"
import NumberTracing from "./components/NumberTracing"
import CountAndColor from "./components/CountAndColor"
import AbacusToNumber from "./components/AbacusToNumber"
import ConnectingLines from "./components/ConnectingLines"
import MatchingFruits from "./components/MatchingFruits"
import ColoringBeads from "./components/ColoringBeads"
import SelectingFruits from "./components/SelectingFruits"
import StroopTest from "./components/StroopTest"

const activities = [
  {
    id: 0,
    title: "Знакомство с Абакусом",
    description: "Изучаем устройство абакуса и его части",
    icon: "👋",
    color: "card-sky",
  },
  {
    id: 1,
    title: "Абакус - Сложение и Вычитание",
    description: "Учимся складывать и вычитать на абакусе",
    icon: "🧮",
    color: "card-blue",
  },
  {
    id: 2,
    title: "Изучаем цифры 1-4",
    description: "Пишем цифры и считаем предметы",
    icon: "✏️",
    color: "card-green",
  },
  {
    id: 3,
    title: "Изучаем цифры 5-8",
    description: "Продолжаем изучать цифры",
    icon: "📝",
    color: "card-purple",
  },
  {
    id: 4,
    title: "Обводим цифры",
    description: "Тренируем написание цифр по точкам",
    icon: "✍️",
    color: "card-orange",
  },
  {
    id: 5,
    title: "Считаем и раскрашиваем",
    description: "Раскрашиваем нужное количество предметов",
    icon: "🎨",
    color: "card-red",
  },
  {
    id: 6,
    title: "Записываем числа",
    description: "Записываем число, показанное на абакусе",
    icon: "🔢",
    color: "card-yellow",
  },
  {
    id: 7,
    title: "Соединяем линиями",
    description: "Соединяем предметы с числами и абакусом",
    icon: "🔗",
    color: "card-indigo",
  },
  {
    id: 8,
    title: "Устанавливаем соответствие",
    description: "Сопоставляем фрукты с числами",
    icon: "🍓",
    color: "card-pink",
  },
  {
    id: 9,
    title: "Раскрашиваем бусинки",
    description: "Раскрашиваем нужное количество бусинок",
    icon: "🎯",
    color: "card-teal",
  },
  {
    id: 10,
    title: "Подбираем фрукты",
    description: "Соединяем абакус с правильными фруктами",
    icon: "🍎",
    color: "card-lime",
  },
  {
    id: 11,
    title: "Тест Струпа",
    description: "Развиваем внимание и концентрацию",
    icon: "🎯",
    color: "card-cyan",
  },
]

function InteractiveTextbookForPreschoolers({ onClose }) {
  const [currentActivity, setCurrentActivity] = useState(null)
  const [score, setScore] = useState(0)
  const { t } = useLanguage()

  const renderActivity = () => {
    switch (currentActivity) {
      case 0:
        return <AbacusIntroduction onScore={(points) => setScore(score + points)} onFinish={() => setCurrentActivity(null)} />
      case 1:
        return <AbacusArithmetic onScore={(points) => setScore(score + points)} onFinish={() => setCurrentActivity(null)} />
      case 2:
        return <NumberLearning1to4 onScore={(points) => setScore(score + points)} onFinish={() => setCurrentActivity(null)} />
      case 3:
        return <NumberLearning5to8 onScore={(points) => setScore(score + points)} onFinish={() => setCurrentActivity(null)} />
      case 4:
        return <NumberTracing onScore={(points) => setScore(score + points)} onFinish={() => setCurrentActivity(null)} />
      case 5:
        return <CountAndColor onScore={(points) => setScore(score + points)} onFinish={() => setCurrentActivity(null)} />
      case 6:
        return <AbacusToNumber onScore={(points) => setScore(score + points)} onFinish={() => setCurrentActivity(null)} />
      case 7:
        return <ConnectingLines onScore={(points) => setScore(score + points)} onFinish={() => setCurrentActivity(null)} />
      case 8:
        return <MatchingFruits onScore={(points) => setScore(score + points)} onFinish={() => setCurrentActivity(null)} />
      case 9:
        return <ColoringBeads onScore={(points) => setScore(score + points)} onFinish={() => setCurrentActivity(null)} />
      case 10:
        return <SelectingFruits onScore={(points) => setScore(score + points)} onFinish={() => setCurrentActivity(null)} />
      case 11:
        return <StroopTest onScore={(points) => setScore(score + points)} onFinish={() => setCurrentActivity(null)} />
      default:
        return <div>Урок не найден</div>
    }
  }

  if (currentActivity !== null) {
    return (
      <div className="InteractiveTextbookForPreschoolers">
        <div className="lesson-container">
          <div className="lesson-header">
            <button onClick={() => setCurrentActivity(null)} className="back-button">
              ← Назад
            </button>
            <div className="score-badge">Очки: {score} ⭐</div>
          </div>
          {renderActivity()}
        </div>
      </div>
    )
  }

  return (
    <div className="InteractiveTextbookForPreschoolers">
      <div className="flex justify-between items-center">
        <div className="header">
          <h1 className="title">🧮 Ментальная Арифметика 🧮</h1>
          <p className="subtitle">Интерактивный учебник для дошкольников</p>
          <div className="score-badge">Общие очки: {score} ⭐</div>
        </div>
        {/* Закрыть кнопку */}
        {onClose && (
          <button onClick={() => onClose()} className="btn btn-secondary flex items-center space-x-2">
            <Icons.Close className="w-4 h-4" />
            <span>{t("common.close")}</span>
          </button>
        )}
      </div>
      <div className="mt-4">
        <div className="activities-grid">
          {activities.map((activity) => (
            <div
              key={activity.id}
              className={`activity-card ${activity.color}`}
              onClick={() => setCurrentActivity(activity.id)}
            >
              <div className="activity-icon">{activity.icon}</div>
              <h3 className="activity-title">{activity.title}</h3>
              <p className="activity-description">{activity.description}</p>
              <button className="start-button">Начать урок! 🚀</button>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

export default InteractiveTextbookForPreschoolers
